import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { TRACKING_NAVIGATION_ABORT } from '../constants/tracking'

// Tracking scripts can't be unloaded once they're running. When
// needsFullLoad(url) is true for a client-side route change, start a full page
// load and abort the route change, so the new page never renders (or updates
// history) in a document where the script is running. needsFullLoad must keep
// the same identity across renders (define it at module level).
export const useFullPageLoadFor = (needsFullLoad) => {
  const router = useRouter()

  useEffect(() => {
    const handleRouteChangeStart = (url, { shallow }) => {
      if (needsFullLoad(url)) {
        window.location.assign(url)
        router.events.emit(
          'routeChangeError',
          Object.assign(new Error(TRACKING_NAVIGATION_ABORT), { cancelled: true }),
          url,
          { shallow }
        )
        throw TRACKING_NAVIGATION_ABORT
      }
    }

    // next/link doesn't catch router.push() rejections, so the abort above
    // surfaces as an unhandled rejection. Keep it out of the console.
    const handleUnhandledRejection = (event) => {
      if (event.reason === TRACKING_NAVIGATION_ABORT) event.preventDefault()
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [router.events, needsFullLoad])
}
